import { CheckboxOptionType } from 'antd/lib/checkbox';
import { OrganisationGroup } from '../../../../common/api/spidertracks-sdk/private/services/UserService';
import { Member } from './types';

interface GroupsAndMembersInfo {
  selectedGroupIds: string[];
  selectedMemberIds: string[];
  groupsOptions: CheckboxOptionType[];
  membersOptions: CheckboxOptionType[];
}

const checkboxOptionTransformer = (
  memberOrGroup: Member | OrganisationGroup,
  disabled: boolean = false
): CheckboxOptionType => {
  return {
    disabled,
    label: memberOrGroup.name,
    value: memberOrGroup.id
  };
};

export const getGroupsAndMembersFromRecipients = (
  selectedRecipientIds: string[],
  orgGroups: OrganisationGroup[],
  orgMembers: Member[]
): GroupsAndMembersInfo => {
  let selectedGroups: string[] = [];
  let selectedMembers: string[] = [];

  let groupsOptions = orgGroups.map(group => checkboxOptionTransformer(group, false));
  let membersOptions = orgMembers.map(member => checkboxOptionTransformer(member, false));
  const allUsersGroupId = orgGroups.find(group => group.type === 'USERS')?.id;
  const allAdminsGroupId = orgGroups.find(group => group.type === 'ADMINS')?.id;

  if (
    allUsersGroupId &&
    (selectedRecipientIds.includes('*') || selectedRecipientIds.includes(allUsersGroupId))
  ) {
    // Processing: All users
    selectedGroups.push(allUsersGroupId);
    groupsOptions = orgGroups.map(group =>
      checkboxOptionTransformer(group, group.type !== 'USERS')
    );
    selectedMembers.push(...orgMembers.map(member => member.id));
    membersOptions = orgMembers.map(member => checkboxOptionTransformer(member, true));
  } else {
    // Processing: All admins
    if (
      allAdminsGroupId &&
      (selectedRecipientIds.includes('ADMINS') || selectedRecipientIds.includes(allAdminsGroupId))
    ) {
      selectedGroups.push(allAdminsGroupId);
      selectedMembers.push(...orgMembers.filter(member => member.isAdmin).map(member => member.id));
      membersOptions = orgMembers.map(member => checkboxOptionTransformer(member, member.isAdmin));
    }
    // Processing: remaining groups that are not 'All users' or 'All admins'
    const remainingSelectedGroups = selectedRecipientIds.filter(selectedGroup =>
      orgGroups.find(orgGroup => orgGroup.id === selectedGroup)
    );
    selectedGroups.push(...remainingSelectedGroups);
    let remainMembersInGroups: string[] = [];
    remainingSelectedGroups.map(selectedGroup =>
      orgGroups
        .find(orgGroup => orgGroup.id === selectedGroup)
        ?.members?.forEach(member => {
          if (!selectedMembers.includes(member.id)) {
            remainMembersInGroups.push(member.id);
          }
        })
    );
    selectedMembers.push(...remainMembersInGroups);
    membersOptions = membersOptions.map(each => {
      if (remainMembersInGroups.includes(each.value.toString())) {
        return { ...each, disabled: true };
      }
      return { ...each };
    });
    // Processing: remaining members that are not 'All users', 'All admins', nor members in the groups
    const remainingSelectedMembers = selectedRecipientIds
      .filter(selectedRecipient => selectedRecipient !== '*' && selectedRecipient !== 'ADMINS')
      .map(
        selectedRecipient => orgMembers.find(member => member.id === selectedRecipient)?.id || null
      )
      .filter(
        selectedRecipient =>
          !selectedMembers.some(selectedMember => selectedMember === selectedRecipient)
      )
      .filter((id): id is string => !!id);
    selectedMembers.push(...remainingSelectedMembers);
  }

  // Remove duplicates from selectedGroups and selectedMembers
  selectedGroups = [...new Set(selectedGroups)];
  selectedMembers = [...new Set(selectedMembers)];

  return {
    selectedGroupIds: selectedGroups,
    selectedMemberIds: selectedMembers,
    groupsOptions: groupsOptions,
    membersOptions: membersOptions
  };
};
